import { useEffect, useRef, useState } from "react";
import Map from "../../Map/Map";
import API from "../../../Api/Api";
import { API_END_POINT } from "../../../Api/EndPoints";
import { useTranslation } from "react-i18next";
import Select from "../../Common/FormElements/Select/Select";
import { useSelector } from "react-redux";
import _ from "lodash";
import { isDefined } from "../../../Utils";
import { AutoComplete } from "primereact/autocomplete";
import axios from "axios";
import { Spinner } from "react-bootstrap";

const ProspectMap = () => {
  const AREA_STATUS = ["AVAILABLE", "TO_SEIZE", "TAKEN"];
  const { t } = useTranslation("common");
  const [loading, setLoading] = useState(false);
  const [prospects, setProspects] = useState([]);
  const [value, setValue] = useState("");
  const [items, setItems] = useState([]);
  const [formData, setFormData] = useState({
    prospect: AREA_STATUS,
  });

  const { affiliates } = useSelector((state) => state.affiliateReducer);
  const { user } = useSelector((state) => state.userReducer);

  const getAffiliatesOptions = () =>
    affiliates.map((affiliate) => {
      return {
        name: affiliate?.attributes?.company,
        code: affiliate?.id,
      };
    });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const search = _.debounce(async (event) => {
    const { query } = event;

    const filters = {};

    const marque = user?.attributes?.marque?.data?.id;
    const userRole = user?.attributes?.userRole;
    if (userRole === 2) {
      filters.business_affiliate = {
        id: {
          $eq: user?.attributes?.business_affiliate?.data?.id,
        },
      };
    }
    const { data: entities } = await API.get(
      `${API_END_POINT}api/geo-entities`,
      {
        params: {
          statusList: formData?.prospect,
          reseller: formData?.reseller,
          filters: {
            prospect: {
              ...filters,
              marque: {
                id: { $eq: marque },
              },
              companyName: {
                $containsi: query,
              },
            },
          },
          populate: {
            prospect: {
              fields: ["id", "companyName"],
            },
          },
        },
      },
    );

    const { data } = await axios.get(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${query}.json?access_token=${process.env.REACT_APP_MAPBOX_TOKEN}`,
    );

    if (data?.features?.length > 0) {
      setItems([
        ...(entities?.data?.map((entity) => ({
          center: entity?.attributes?.coordinates,
          place_name:
            entity?.attributes?.prospect?.data?.attributes?.companyName,
        })) || []),
        ...(data?.features || []),
      ]);
    } else {
      setItems([]);
    }
  }, 300);

  const abortControllerRef = useRef(null);

  async function fetch(signal) {
    try {
      setLoading(true);

      const filters = [];
      const searchFilters = [];

      if (isDefined(formData.business_affiliate?.code)) {
        filters.push({
          $or: [
            {
              reseller: {
                business_affiliate: {
                  id: {
                    $eq: formData.business_affiliate?.code,
                  },
                },
              },
            },

            {
              prospect: {
                business_affiliate: {
                  id: {
                    $eq: formData.business_affiliate?.code,
                  },
                },
              },
            },
          ],
        });
      }

      const userRole = user?.attributes?.userRole;

      if (userRole === 2) {
        filters.push({
          $or: [
            {
              reseller: {
                business_affiliate: {
                  id: {
                    $eq: user?.attributes?.business_affiliate?.data?.id,
                  },
                },
              },
            },
            {
              prospect: {
                business_affiliate: {
                  id: {
                    $eq: user?.attributes?.business_affiliate?.data?.id,
                  },
                },
              },
            },
          ],
        });
      }

      const marque = user?.attributes?.marque?.data?.id;

      const { data } = await API.get(`${API_END_POINT}api/geo-entities`, {
        signal,
        params: {
          statusList: formData?.prospect,
          reseller: formData?.reseller,
          fields: ["id", "markers", "turfs"],
          populate: {
            reseller: {
              populate: {
                note: true,
                contract: true,
              },
            },
            prospect: {
              populate: {
                note: true,
              },
            },
          },
          filters: {
            $and: [
              ...(filters || []),
              ...(searchFilters || []),
              {
                $or: [
                  {
                    reseller: {
                      marque: { id: { $eq: marque } },
                    },
                  },
                  {
                    prospect: {
                      marque: { id: { $eq: marque } },
                    },
                  },
                ],
              },
            ],
          },
        },
      });

      const result = data?.data?.map((entity) => {
        const markers = entity?.attributes?.markers;
        const turfs = entity?.attributes?.turfs;
        const reseller = entity?.attributes?.reseller?.data;
        const prospect = entity?.attributes?.prospect?.data;
        return {
          id: entity?.id,
          markers,
          turfs,
          areaStatus: entity?.attributes?.areaStatus,
          name: prospect?.attributes?.companyName ?? reseller?.attributes?.name,
          ...(reseller?.attributes ?? prospect?.attributes ?? {}),
          isProspect: !!prospect,
        };
      });

      setProspects(result || []);
    } catch (error) {
      if (error.name === "CanceledError" || error.code === "ERR_CANCELED")
        return;
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    fetch(abortControllerRef.current.signal);
  }, [formData, user]);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  return (
    <div className="position-relative z-0">
      {loading && (
        <div className="position-absolute top-0 start-0 w-100 h-100 bg-white bg-opacity-50 z-9999 d-flex align-items-center justify-content-center">
          <Spinner />
        </div>
      )}
      <h2 className="text-blue bold f22 quickSand mt-5 mb-3">
        {t("Contract Catchment Area Map")}
      </h2>
      <div className="d-flex mb-3 flex-wrap gap-3">
        <div className="">
          <AutoComplete
            field="place_name"
            placeholder={t("Search")}
            className="customAutocomplete br6 w-100 z-1"
            value={value}
            suggestions={items}
            completeMethod={search}
            onChange={(e) => setValue(e.value)}
            itemTemplate={(item) => {
              return (
                <div className="d-flex flex-column">
                  <span className="f16 bold">{item?.place_name}</span>
                  {item?.context?.length > 0 && (
                    <span className="f14">{item?.context[0]?.text}</span>
                  )}
                </div>
              );
            }}
            // panelClassName="w-100"
            style={{
              height: "40px",
              border: "none",
              outline: "none",
            }}
          />
        </div>
        {user?.attributes?.userRole === 1 && (
          <div className="max-w-200px w-100">
            <Select
              name="business_affiliate"
              value={formData.business_affiliate}
              label={t("Manager")}
              placeholder={t("Manager")}
              filter
              appendToSelf
              showClear
              resetable
              options={getAffiliatesOptions()}
              callback={handleChange}
            />
          </div>
        )}
        <div className="w-100 max-w-200px">
          <button
            className={`w-100 h-40px br5 ${
              formData?.reseller === false
                ? "border-blue-700 border-2 text-blue-700 bg-white"
                : "bg-blue-700 border-0 text-white"
            }`}
            onClick={() => {
              setFormData((prev) => ({
                ...prev,
                reseller: prev?.reseller === false,
              }));
            }}
          >
            {t("Store")}
          </button>
        </div>
        <div className="w-100 max-w-200px">
          <button
            className={`w-100 h-40px br5 ${
              formData?.prospect?.includes(AREA_STATUS[0])
                ? "bg-green-600 border-0 text-white"
                : "border-green-600 border-2 text-green-600 bg-white"
            }`}
            onClick={() => {
              setFormData((prev) => ({
                ...prev,
                prospect: prev?.prospect?.includes(AREA_STATUS[0])
                  ? prev?.prospect?.filter((p) => p !== AREA_STATUS[0])
                  : [...prev?.prospect, AREA_STATUS[0]],
              }));
            }}
          >
            {t("Prospect O Free")}
          </button>
        </div>
        <div className="w-100 max-w-200px">
          <button
            className={`w-100 h-40px br5 ${
              formData?.prospect?.includes(AREA_STATUS[1])
                ? "bg-orange-500 border-0 text-white"
                : "border-orange-500 border-2 text-orange-500 bg-white"
            }`}
            onClick={() => {
              setFormData((prev) => ({
                ...prev,
                prospect: prev?.prospect?.includes(AREA_STATUS[1])
                  ? prev?.prospect?.filter((p) => p !== AREA_STATUS[1])
                  : [...prev?.prospect, AREA_STATUS[1]],
              }));
            }}
          >
            {t("Prospect O To Seize")}
          </button>
        </div>
        <div className="w-100 max-w-200px">
          <button
            className={`w-100 h-40px br5 ${
              formData?.prospect?.includes(AREA_STATUS[2])
                ? "bg-red-600 border-0 text-white"
                : "border-red-600 border-2 text-red-600 bg-white"
            }`}
            onClick={() => {
              setFormData((prev) => ({
                ...prev,
                prospect: prev?.prospect?.includes(AREA_STATUS[2])
                  ? prev?.prospect?.filter((p) => p !== AREA_STATUS[2])
                  : [...prev?.prospect, AREA_STATUS[2]],
              }));
            }}
          >
            {t("Prospect O Taken")}
          </button>
        </div>
      </div>
      <div className="position-relative">
        <button
          className="position-absolute top-0 start-0 p-3 border-0 h-60px w-60px d-flex align-items-center justify-content-center z-9999 f20 bg-transparent"
          onClick={fetch}
        >
          <i class="bi bi-arrow-clockwise f30 text-blue-700"></i>
        </button>

        <Map features={prospects} currentCenter={value?.center} />
      </div>
    </div>
  );
};

export default ProspectMap;
